import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, compare as compareApi, type CompareResult } from "@/lib/api";
import { UpgradePrompt, isPlanLimit } from "@/components/UpgradePrompt";

/**
 * The front door for someone who has nothing yet.
 *
 * The old empty state said "No documents yet — create your first document to
 * start collaborating", which asks for the one concept new users report as
 * confusing, and leads to a second screen telling them to paste a prompt into
 * an agent they may not have connected. Two unexplained steps before anything
 * happens.
 *
 * This asks for text and returns three model answers, saved. No connector,
 * no API key, and no vocabulary to learn: the copy describes what you can
 * *do* with the result rather than naming the object. Introducing a second
 * noun ("thread") for something the URL, API, tools and database all call a
 * document would teach a distinction the system doesn't have.
 *
 * It is also the one thing a model router can't reproduce: the answers
 * persist, attributed, somewhere another agent can extend.
 */
export function TryItPanel() {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [focus, setFocus] = useState("");
  const [result, setResult] = useState<CompareResult | null>(null);

  const runMut = useMutation({
    mutationFn: (visibility?: "public") =>
      compareApi.run({
        text,
        prompt: focus.trim() || undefined,
        title: "Model comparison",
        ...(visibility ? { visibility } : {}),
      }),
    onSuccess: (res) => {
      setResult(res);
      void qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });

  const tooShort = text.trim().length < 12;

  if (result) {
    return (
      <section className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5 sm:p-6">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-lg font-semibold">
            {result.answers.length} models answered
          </h2>
          <button
            type="button"
            onClick={() => {
              setResult(null);
              setText("");
              setFocus("");
            }}
            className="text-xs text-zinc-500 hover:underline"
          >
            Ask something else
          </button>
        </div>

        <div className="space-y-3 mb-4">
          {result.answers.map((a) => (
            <div
              key={a.model_id}
              className="rounded-md border border-zinc-200 dark:border-zinc-800 p-3"
            >
              <div className="text-xs font-medium text-zinc-500 mb-1">
                {a.model_name}
              </div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed">
                {a.review.trim()}
              </div>
            </div>
          ))}
        </div>

        {result.failures.length > 0 && (
          <div className="text-xs text-amber-700 dark:text-amber-400 mb-3">
            {result.failures.length} model
            {result.failures.length === 1 ? "" : "s"} didn't answer (
            {result.failures.map((f) => f.model_id).join(", ")}). You weren't
            charged for those.
          </div>
        )}

        {result.persisted && result.session_id ? (
          <div className="rounded-md bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 p-3 text-sm">
            <div className="mb-1">
              Saved and private to you —{" "}
              <Link to={`/d/${result.session_id}`} className="underline font-medium">
                open it
              </Link>
              .
            </div>
            <div className="text-xs text-zinc-500">
              You can add your own notes, ask another model to weigh in, or
              invite someone to it.
            </div>
          </div>
        ) : (
          <div className="text-xs text-amber-700 dark:text-amber-400">
            The answers came back but couldn't be saved. Copy anything you
            want to keep.
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5 sm:p-6">
      <h2 className="text-lg font-semibold mb-1">
        Ask three models the same question
      </h2>
      <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
        Paste some code, a draft, or a question. GPT, Claude and Gemini each
        answer, and you keep the comparison. Nothing to set up.
      </p>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        maxLength={100_000}
        placeholder={"def retry(fn, n=3):\n    for i in range(n):\n        try:\n            return fn()\n        except Exception:\n            pass"}
        className="w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-3 py-2 text-sm font-mono mb-2"
      />
      <input
        type="text"
        value={focus}
        onChange={(e) => setFocus(e.target.value)}
        maxLength={200}
        placeholder="Optional: what should they focus on?"
        className="w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-1.5 text-sm mb-3"
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => runMut.mutate(undefined)}
          disabled={runMut.isPending || tooShort}
          className="rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {runMut.isPending ? "Asking three models…" : "Compare 3 models"}
        </button>
        <span className="text-xs text-zinc-500">
          {runMut.isPending
            ? "This takes a few seconds."
            : "Free on your trial allowance."}
        </span>
      </div>

      {/* A comparison is saved private by default, so a free user past the
          private-document limit lands here. Offer the free way out first —
          publishing costs them nothing and keeps them moving — with upgrade
          alongside it. */}
      {isPlanLimit(runMut.error) && (
        <div className="mt-3">
          <UpgradePrompt
            error={runMut.error}
            publicLabel="Save it publicly instead"
            onMakePublic={() => runMut.mutate("public")}
          />
        </div>
      )}
      {runMut.error && !isPlanLimit(runMut.error) && (
        <div className="text-xs text-red-600 dark:text-red-400 mt-3">
          {(runMut.error as ApiError).body
            ? ((runMut.error as ApiError).body as { error?: string }).error
            : String(runMut.error)}
        </div>
      )}

      <p className="text-xs text-zinc-500 mt-4 pt-3 border-t border-zinc-200 dark:border-zinc-800">
        Prefer to work from your agent? Connect Claude, ChatGPT or Codex on the{" "}
        <Link to="/tokens" className="underline">
          connectors page
        </Link>
        , or start a blank document above.
      </p>
    </section>
  );
}
